/* eslint-disable max-lines -- one route table: the admin app routes */
import { adminOpsRoutes, type AdminOpsPathConfig } from './adminOpsRoutes';
import {
  BrandTab,
  OrganizationTab,
  ServiceAreaTab,
  ServiceDepartmentGroupTab,
  ServiceOrganizationTab,
  ShopSupplierTab,
  SiteGroupTab,
  UnableToCompleteCatalogEntryTab,
  UserGroupTab
} from './adminRouteParams';
import { adminTireHotelAppRoutes, type AdminTireHotelPathConfig } from './adminTireHotelAppRoutes';
import { partnerAdapterRoutes, type PartnerAdapterPathConfig } from './partnerAdapterRoutes';
import { defineRoutes } from './types';
import { getUrlWithQueryParams, type FilterSearch } from './utils';

type StringOrNumber = string | number;

interface PathConfig extends AdminOpsPathConfig, AdminTireHotelPathConfig, PartnerAdapterPathConfig {
  invoiceDetail: { id: StringOrNumber; invoiceId: StringOrNumber };
  vehicleCatalogNode: { nodeId: StringOrNumber };
  editServiceArea: { id: StringOrNumber; tab?: ServiceAreaTab };
  serviceOrganization: { id: StringOrNumber; tab?: ServiceOrganizationTab };
  serviceDepartmentCreate: { serviceOrgId: StringOrNumber };
  serviceDepartment: { serviceOrgId: StringOrNumber; id: StringOrNumber };
  serviceDepartmentGroup: { serviceOrgId: StringOrNumber; id: StringOrNumber; tab?: ServiceDepartmentGroupTab };
  siteGroup: { serviceOrgId: StringOrNumber; id: StringOrNumber; tab?: SiteGroupTab };
  serviceDepartmentModule: { module: string };
  overtimeConfigCreate: { serviceOrgId: StringOrNumber };
  overtimeConfigEdit: { serviceOrgId: StringOrNumber; id: StringOrNumber };
  accountDetail: { id: StringOrNumber };
  accountDuplicates: undefined;
  editSalesItem: { id: StringOrNumber };
  editUserGroup: { id: StringOrNumber; tab?: UserGroupTab; 'tire-hotel-id'?: StringOrNumber };
  userGroupBookings: { id: StringOrNumber };
  editUserGroupsOrder: { userGroupId: StringOrNumber; bookingId: StringOrNumber };
  bookingProposal: { id: StringOrNumber };
  editCapability: { id: StringOrNumber };
  skill: { id: StringOrNumber };
  brandEdit: { brandId: StringOrNumber; tab?: BrandTab };
  brandCampaignDetail: { brandId: StringOrNumber; campaignId: StringOrNumber };
  brandCampaignCreate: { brandId: StringOrNumber };
  brandCouponDetail: { brandId: StringOrNumber; couponId: StringOrNumber };
  brandCouponCreate: { brandId: StringOrNumber };
  brandMembershipProgramDetail: { brandId: StringOrNumber; programId: StringOrNumber };
  brandMembershipProgramCreate: { brandId: StringOrNumber };
  editWorker: { id: StringOrNumber };
  editOrganization: { id: StringOrNumber; tab?: OrganizationTab };
  department: { organizationId: StringOrNumber; departmentId: StringOrNumber };
  editDepartment: { id: StringOrNumber };
  editAddress: { id: StringOrNumber };
  parkingPlace: { id: StringOrNumber };
  apiKey: { id: StringOrNumber };
  smsDetail: { id: StringOrNumber };
  emailDetail: { id: StringOrNumber };
  shortUrlDetail: { code: string };
  unableToCompleteCatalogEntry: { id: StringOrNumber; tab?: UnableToCompleteCatalogEntryTab };
  carTireOEMarkDetail: { id: StringOrNumber };
  wheelLockCatalogDetail: { id: StringOrNumber };
  noticeDetail: { id: StringOrNumber };
  noticeTemplateDetail: { templateId: StringOrNumber };
  tireBrandDetail: { id: StringOrNumber };
  shopSupplierDetail: { id: StringOrNumber; tab?: ShopSupplierTab };
  shopSupplierTireBrandDetail: { supplierId: StringOrNumber; categoryId: StringOrNumber };
  shopSupplierRimBrandDetail: { supplierId: StringOrNumber; categoryId: StringOrNumber };
  dashboardWidgetDefinition: { id: StringOrNumber };
  salesItemCatalogEntry: { id: StringOrNumber };
  /** The car list's filters (`filterSearch`). */
  cars: { filters?: FilterSearch };
  carDetail: { carId: StringOrNumber };
  salesItemOffer: { id: StringOrNumber };
  iamRole: { roleId: string };
  routeSettlementDetail: { id: StringOrNumber };
  payrollDetail: { id: StringOrNumber };
  standaloneInvoiceDetail: { invoiceId: StringOrNumber };
  invoiceCreate: { userGroupId?: StringOrNumber };
  dinteroSessionProfile: { serviceOrgId: StringOrNumber; profileId: string };
  createDinteroSessionProfile: { serviceOrgId: StringOrNumber };
  loginBlocklistDetail: { id: StringOrNumber };
  ticketDetail: { id: StringOrNumber };
  supportConversation: { id: StringOrNumber };
  supportMailboxDetail: { id: StringOrNumber };
  supportScheduleDetail: { id: StringOrNumber };
  editSupportSchedule: { id: StringOrNumber };
  signageDeviceDetail: { id: StringOrNumber };
  signagePair: { code?: string };
}

export const adminRoutes = defineRoutes<PathConfig>()({
  ...adminOpsRoutes,
  ...adminTireHotelAppRoutes,
  ...partnerAdapterRoutes,
  unableToCompleteReasons: {
    getPath: () => `/unable-to-complete-reasons`,
    getBasePath: () => '/unable-to-complete-reasons'
  },
  createUnableToCompleteCatalogEntry: {
    getPath: () => `/unable-to-complete-reasons/create`,
    getBasePath: () => '/unable-to-complete-reasons/create'
  },
  unableToCompleteCatalogEntry: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/unable-to-complete-reasons/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/unable-to-complete-reasons/:id'
  },
  cars: {
    getPath: (queryParams = {}) =>
      getUrlWithQueryParams({
        path: '/cars',
        queryParams
      }),
    getBasePath: () => '/cars'
  },
  carDetail: {
    getPath: (data) => `/cars/${data.carId}`,
    getBasePath: () => '/cars/:carId'
  },
  addresses: {
    getPath: () => `/addresses`,
    getBasePath: () => '/addresses'
  },
  parkingPlaces: {
    getPath: () => '/sites/parking-places',
    getBasePath: () => '/sites/parking-places'
  },
  parkingPlace: {
    getPath: ({ id }) => `/sites/parking-places/${id}`,
    getBasePath: () => '/sites/parking-places/:id'
  },
  editAddress: {
    getPath: (data) => `/addresses/${data.id}`,
    getBasePath: () => '/addresses/:id'
  },
  planningOverview: {
    getPath: () => `/planning-overview`,
    getBasePath: () => '/planning-overview'
  },
  organizations: {
    getPath: () => `/organizations`,
    getBasePath: () => '/organizations'
  },
  editOrganization: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/organizations/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/organizations/:id'
  },
  createOrganization: {
    getPath: () => `/organizations/create`,
    getBasePath: () => '/organizations/create'
  },
  departments: {
    getPath: () => `/departments`,
    getBasePath: () => '/departments'
  },
  department: {
    getPath: (data) => `/organizations/${data.organizationId}/departments/${data.departmentId}`,
    getBasePath: () => '/organizations/:organizationId/departments/:departmentId'
  },
  editDepartment: {
    getPath: (data) => `/departments/${data.id}`,
    getBasePath: () => '/departments/:id'
  },
  invoiceDetail: {
    getPath: (data) => `/user-groups/${data.id}/invoices/${data.invoiceId}`,
    getBasePath: () => '/user-groups/:id/invoices/:invoiceId'
  },
  vehicleCatalog: {
    getPath: () => `/vehicle-catalog`,
    getBasePath: () => '/vehicle-catalog'
  },
  vehicleCatalogNode: {
    getPath: ({ nodeId }) => `/vehicle-catalog/${nodeId}`,
    getBasePath: () => '/vehicle-catalog/:nodeId'
  },
  vehicleCatalogGaps: {
    getPath: () => `/vehicle-catalog/data-gaps`,
    getBasePath: () => '/vehicle-catalog/data-gaps'
  },
  serviceAreas: {
    getPath: () => `/service-areas`,
    getBasePath: () => '/service-areas'
  },
  createServiceArea: {
    getPath: () => `/service-areas/create`,
    getBasePath: () => '/service-areas/create'
  },
  editServiceArea: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/service-areas/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/service-areas/:id'
  },
  serviceOrganizations: {
    getPath: () => `/service-organizations`,
    getBasePath: () => `/service-organizations`
  },
  createServiceOrganization: {
    getPath: () => `/service-organizations/create`,
    getBasePath: () => `/service-organizations/create`
  },
  serviceOrganization: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/service-organizations/${id}`,
        queryParams: rest
      }),
    getBasePath: () => `/service-organizations/:id`
  },
  catchAll: {
    getPath: () => `/*`,
    getBasePath: () => `/*`
  },
  home: {
    getPath: () => `/`,
    getBasePath: () => '/'
  },
  errorPage: {
    getPath: () => `/error`,
    getBasePath: () => '/error'
  },
  login: {
    getPath: () => `/login`,
    getBasePath: () => '/login'
  },
  resetPassword: {
    getPath: () => `/reset-password`,
    getBasePath: () => '/reset-password'
  },
  accountDetail: {
    getPath: (data) => `/accounts/${data.id}`,
    getBasePath: () => '/accounts/:id'
  },
  accounts: {
    getPath: () => `/accounts`,
    getBasePath: () => '/accounts'
  },
  accountDuplicates: {
    getPath: () => `/accounts/duplicates`,
    getBasePath: () => '/accounts/duplicates'
  },
  createAccount: {
    getPath: () => `/accounts/create`,
    getBasePath: () => '/accounts/create'
  },
  salesItems: {
    getPath: () => `/sales-items`,
    getBasePath: () => '/sales-items'
  },
  editSalesItem: {
    getPath: (data) => `/sales-items/${data.id}`,
    getBasePath: () => '/sales-items/:id'
  },
  createSalesItem: {
    getPath: () => `/sales-items/create`,
    getBasePath: () => '/sales-items/create'
  },
  userGroups: {
    getPath: () => `/user-groups`,
    getBasePath: () => '/user-groups'
  },
  editUserGroup: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/user-groups/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/user-groups/:id'
  },
  createUserGroup: {
    getPath: () => `/user-groups/create`,
    getBasePath: () => '/user-groups/create'
  },

  userGroupBookings: {
    getPath: (data) => `/user-groups/${data.id}/bookings`,
    getBasePath: () => '/user-groups/:id/bookings'
  },

  editUserGroupsOrder: {
    getPath: (data) => `/user-groups/${data.userGroupId}/bookings/${data.bookingId}/edit-order`,
    getBasePath: () => '/user-groups/:userGroupId/bookings/:bookingId/edit-order'
  },

  bookings: {
    getPath: () => `/bookings`,
    getBasePath: () => '/bookings'
  },
  bookingProposals: {
    getPath: () => `/booking-proposals`,
    getBasePath: () => '/booking-proposals'
  },
  bookingProposal: {
    getPath: (data) => `/booking-proposals/${data.id}`,
    getBasePath: () => '/booking-proposals/:id'
  },
  serviceCategories: {
    getPath: () => `/service-categories`,
    getBasePath: () => '/service-categories'
  },
  salesItemTags: {
    getPath: () => `/sales-item-tags`,
    getBasePath: () => '/sales-item-tags'
  },
  serviceDepartmentCreate: {
    getPath: ({ serviceOrgId }) => `/service-organizations/${serviceOrgId}/service-departments/create`,
    getBasePath: () => '/service-organizations/:serviceOrgId/service-departments/create'
  },
  serviceDepartment: {
    getPath: ({ serviceOrgId, id }) => `/service-organizations/${serviceOrgId}/service-departments/${id}`,
    getBasePath: () => '/service-organizations/:serviceOrgId/service-departments/:id'
  },
  serviceDepartmentGroup: {
    getPath: ({ serviceOrgId, id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/service-organizations/${serviceOrgId}/service-department-groups/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/service-organizations/:serviceOrgId/service-department-groups/:id'
  },
  siteGroup: {
    getPath: ({ serviceOrgId, id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/service-organizations/${serviceOrgId}/site-groups/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/service-organizations/:serviceOrgId/site-groups/:id'
  },
  serviceDepartmentModules: {
    getPath: () => `/service-department-modules`,
    getBasePath: () => '/service-department-modules'
  },
  serviceDepartmentModule: {
    getPath: ({ module }) => `/service-department-modules/${module}`,
    getBasePath: () => '/service-department-modules/:module'
  },
  overtimeConfigCreate: {
    getPath: ({ serviceOrgId }) => `/service-organizations/${serviceOrgId}/overtime-configs/create`,
    getBasePath: () => '/service-organizations/:serviceOrgId/overtime-configs/create'
  },
  overtimeConfigEdit: {
    getPath: ({ serviceOrgId, id }) => `/service-organizations/${serviceOrgId}/overtime-configs/${id}`,
    getBasePath: () => '/service-organizations/:serviceOrgId/overtime-configs/:id'
  },
  discounts: {
    getPath: () => `/discounts`,
    getBasePath: () => '/discounts'
  },

  routeDragNDrop: {
    getPath: () => `/route-drag-and-drop`,
    getBasePath: () => '/route-drag-and-drop'
  },
  payments: {
    getPath: () => `/payments`,
    getBasePath: () => '/payments'
  },
  profile: {
    getPath: () => `/profile`,
    getBasePath: () => '/profile'
  },
  devSettings: {
    getPath: () => `/dev-settings`,
    getBasePath: () => '/dev-settings'
  },
  capabilities: {
    getPath: () => `/capabilities`,
    getBasePath: () => '/capabilities'
  },
  createCapability: {
    getPath: () => `/capabilities/create`,
    getBasePath: () => '/capabilities/create'
  },
  editCapability: {
    getPath: (data) => `/capabilities/${data.id}`,
    getBasePath: () => '/capabilities/:id'
  },
  skills: {
    getPath: () => `/skills`,
    getBasePath: () => '/skills'
  },
  skill: {
    getPath: (data) => `/skills/${data.id}`,
    getBasePath: () => '/skills/:id'
  },
  workers: {
    getPath: () => `/service-workers`,
    getBasePath: () => `/service-workers`
  },
  editWorker: {
    getPath: (data) => `/service-workers/${data.id}`,
    getBasePath: () => `/service-workers/:id`
  },
  brands: {
    getPath: () => '/brands',
    getBasePath: () => '/brands'
  },
  brandEdit: {
    getPath: ({ brandId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/brands/${brandId}`,
        queryParams: rest
      }),
    getBasePath: () => '/brands/:id'
  },
  brandCreate: {
    getPath: () => `/brands/create`,
    getBasePath: () => '/brands/create'
  },
  brandCampaignDetail: {
    getPath: ({ brandId, campaignId }) => `/brands/${brandId}/campaigns/${campaignId}`,
    getBasePath: () => '/brands/:brandId/campaigns/:campaignId'
  },
  brandCampaignCreate: {
    getPath: ({ brandId }) => `/brands/${brandId}/campaigns/create`,
    getBasePath: () => '/brands/:brandId/campaigns/create'
  },
  brandCouponDetail: {
    getPath: ({ brandId, couponId }) => `/brands/${brandId}/coupons/${couponId}`,
    getBasePath: () => '/brands/:brandId/coupons/:couponId'
  },
  brandCouponCreate: {
    getPath: ({ brandId }) => `/brands/${brandId}/coupons/create`,
    getBasePath: () => '/brands/:brandId/coupons/create'
  },
  brandMembershipProgramDetail: {
    getPath: ({ brandId, programId }) => `/brands/${brandId}/memberships/${programId}`,
    getBasePath: () => '/brands/:brandId/memberships/:programId'
  },
  brandMembershipProgramCreate: {
    getPath: ({ brandId }) => `/brands/${brandId}/memberships/create`,
    getBasePath: () => '/brands/:brandId/memberships/create'
  },
  apiKeys: {
    getPath: () => `/api-keys`,
    getBasePath: () => '/api-keys'
  },
  apiKey: {
    getPath: (data) => `/api-keys/${data.id}`,
    getBasePath: () => '/api-keys/:id'
  },
  createApiKey: {
    getPath: () => `/api-keys/create`,
    getBasePath: () => '/api-keys/create'
  },
  signageDevices: {
    getPath: () => `/signage`,
    getBasePath: () => '/signage'
  },
  signageDeviceUpdates: {
    getPath: () => `/signage/updates`,
    getBasePath: () => '/signage/updates'
  },
  signageDeviceDetail: {
    getPath: (data) => `/signage/${data.id}`,
    getBasePath: () => '/signage/:id'
  },
  signagePair: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: `/signage/pair`,
        queryParams: data?.code ? { code: data.code } : {}
      }),
    getBasePath: () => '/signage/pair'
  },
  sms: {
    getPath: () => `/sms`,
    getBasePath: () => '/sms'
  },
  smsDetail: {
    getPath: (data) => `/sms/${data.id}`,
    getBasePath: () => '/sms/:id'
  },
  emails: {
    getPath: () => '/communications/emails',
    getBasePath: () => '/communications/emails'
  },
  emailDetail: {
    getPath: (data) => `/communications/emails/${data.id}`,
    getBasePath: () => '/communications/emails/:id'
  },
  shortUrls: {
    getPath: () => '/communications/short-urls',
    getBasePath: () => '/communications/short-urls'
  },
  shortUrlDetail: {
    getPath: (data) => `/communications/short-urls/${data.code}`,
    getBasePath: () => '/communications/short-urls/:code'
  },
  communicationsOverview: {
    getPath: () => '/communications/overview',
    getBasePath: () => '/communications/overview'
  },
  pushNotifications: {
    getPath: () => '/communications/push-notifications',
    getBasePath: () => '/communications/push-notifications'
  },
  notices: {
    getPath: () => '/communications/notices',
    getBasePath: () => '/communications/notices'
  },
  noticeCreate: {
    getPath: () => '/communications/notices/create',
    getBasePath: () => '/communications/notices/create'
  },
  noticeDetail: {
    getPath: (data) => `/communications/notices/${data.id}`,
    getBasePath: () => '/communications/notices/:id'
  },
  noticeTemplates: {
    getPath: () => '/communications/notices/templates',
    getBasePath: () => '/communications/notices/templates'
  },
  noticeTemplateCreate: {
    getPath: () => '/communications/notices/templates/create',
    getBasePath: () => '/communications/notices/templates/create'
  },
  noticeTemplateDetail: {
    getPath: (data) => `/communications/notices/templates/${data.templateId}`,
    getBasePath: () => '/communications/notices/templates/:templateId'
  },
  carTireOEMarks: {
    getPath: () => `/car-tire-original-equipment-marks`,
    getBasePath: () => '/car-tire-original-equipment-marks'
  },
  carTireOEMarkDetail: {
    getPath: (data) => `/car-tire-original-equipment-marks/${data.id}`,
    getBasePath: () => '/car-tire-original-equipment-marks/:id'
  },
  carTireOEMarkCreate: {
    getPath: () => `/car-tire-original-equipment-marks/create`,
    getBasePath: () => '/car-tire-original-equipment-marks/create'
  },
  wheelLockCatalog: {
    getPath: () => '/wheel-lock-catalog',
    getBasePath: () => '/wheel-lock-catalog'
  },
  wheelLockCatalogDetail: {
    getPath: (data) => `/wheel-lock-catalog/${data.id}`,
    getBasePath: () => '/wheel-lock-catalog/:id'
  },
  wheelLockCatalogCreate: {
    getPath: () => '/wheel-lock-catalog/create',
    getBasePath: () => '/wheel-lock-catalog/create'
  },
  tireBrands: {
    getPath: () => `/tire-brands`,
    getBasePath: () => '/tire-brands'
  },
  tireBrandDetail: {
    getPath: (data) => `/tire-brands/${data.id}`,
    getBasePath: () => '/tire-brands/:id'
  },
  rimBrands: {
    getPath: () => `/rim-brands`,
    getBasePath: () => '/rim-brands'
  },
  shopSuppliers: {
    getPath: () => '/shop-suppliers',
    getBasePath: () => '/shop-suppliers'
  },
  shopSupplierDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/shop-suppliers/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/shop-suppliers/:id'
  },
  shopSupplierTireBrandDetail: {
    getPath: (data) => `/shop-suppliers/${data.supplierId}/tire-brand-categories/${data.categoryId}`,
    getBasePath: () => '/shop-suppliers/:supplierId/tire-brand-categories/:categoryId'
  },
  shopSupplierRimBrandDetail: {
    getPath: (data) => `/shop-suppliers/${data.supplierId}/rim-brand-categories/${data.categoryId}`,
    getBasePath: () => '/shop-suppliers/:supplierId/rim-brand-categories/:categoryId'
  },
  dashboardWidgetDefinitions: {
    getPath: () => '/dashboard-widget-definitions',
    getBasePath: () => '/dashboard-widget-definitions'
  },
  createDashboardWidgetDefinition: {
    getPath: () => '/dashboard-widget-definitions/create',
    getBasePath: () => '/dashboard-widget-definitions/create'
  },
  dashboardWidgetDefinition: {
    getPath: (data) => `/dashboard-widget-definitions/${data.id}`,
    getBasePath: () => '/dashboard-widget-definitions/:id'
  },
  orders: {
    getPath: () => '/orders',
    getBasePath: () => '/orders'
  },
  unpaidOrders: {
    getPath: () => '/orders/unpaid',
    getBasePath: () => '/orders/unpaid'
  },
  auditLogs: {
    getPath: () => '/audit-logs',
    getBasePath: () => '/audit-logs'
  },
  securityVerify: {
    getPath: () => '/security/verify',
    getBasePath: () => '/security/verify'
  },
  loginAttempts: {
    getPath: () => '/security/login-attempts',
    getBasePath: () => '/security/login-attempts'
  },
  loginBlocklist: {
    getPath: () => '/security/login-blocklist',
    getBasePath: () => '/security/login-blocklist'
  },
  twilioSafeList: {
    getPath: () => '/security/twilio-safe-list',
    getBasePath: () => '/security/twilio-safe-list'
  },
  temporaryStates: {
    getPath: () => '/security/temporary-states',
    getBasePath: () => '/security/temporary-states'
  },
  loginBlocklistDetail: {
    getPath: (data: { id: number }) => `/security/login-blocklist/${data.id}`,
    getBasePath: () => '/security/login-blocklist/:id'
  },
  createLoginBlocklistEntry: {
    getPath: () => '/security/login-blocklist/create',
    getBasePath: () => '/security/login-blocklist/create'
  },
  salesItemCatalog: {
    getPath: () => '/sales-item-catalog',
    getBasePath: () => '/sales-item-catalog'
  },
  createSalesItemCatalogEntry: {
    getPath: () => '/sales-item-catalog/create',
    getBasePath: () => '/sales-item-catalog/create'
  },
  salesItemCatalogEntry: {
    getPath: (data) => `/sales-item-catalog/${data.id}`,
    getBasePath: () => '/sales-item-catalog/:id'
  },
  salesItemOffers: {
    getPath: () => '/sales-item-offers',
    getBasePath: () => '/sales-item-offers'
  },
  salesItemOffer: {
    getPath: (data) => `/sales-item-offers/${data.id}`,
    getBasePath: () => '/sales-item-offers/:id'
  },
  iamRoles: {
    getPath: () => '/iam/roles',
    getBasePath: () => '/iam/roles'
  },
  iamRoleCreate: {
    getPath: () => '/iam/roles/create',
    getBasePath: () => '/iam/roles/create'
  },
  iamRole: {
    getPath: (data) => `/iam/roles/${encodeURIComponent(data.roleId)}`,
    getBasePath: () => '/iam/roles/:roleId'
  },
  iamPolicies: {
    getPath: () => '/iam/policies',
    getBasePath: () => '/iam/policies'
  },
  iamPolicyCreate: {
    getPath: () => '/iam/policies/create',
    getBasePath: () => '/iam/policies/create'
  },
  routeSettlements: {
    getPath: () => '/route-settlements',
    getBasePath: () => '/route-settlements'
  },
  routeSettlementDetail: {
    getPath: (data) => `/route-settlements/${data.id}`,
    getBasePath: () => '/route-settlements/:id'
  },
  payrolls: {
    getPath: () => '/payrolls',
    getBasePath: () => '/payrolls'
  },
  payrollDetail: {
    getPath: (data) => `/payrolls/${data.id}`,
    getBasePath: () => '/payrolls/:id'
  },
  routeStats: {
    getPath: () => '/routes/stats',
    getBasePath: () => '/routes/stats'
  },
  invoices: {
    getPath: () => '/invoices',
    getBasePath: () => '/invoices'
  },
  // Static segment, so React Router ranks it above /invoices/:invoiceId.
  invoiceCreate: {
    getPath: (data) => getUrlWithQueryParams({ path: '/invoices/create', queryParams: data }),
    getBasePath: () => '/invoices/create'
  },
  standaloneInvoiceDetail: {
    getPath: (data) => `/invoices/${data.invoiceId}`,
    getBasePath: () => '/invoices/:invoiceId'
  },
  paymentsOverview: {
    getPath: () => '/payments/overview',
    getBasePath: () => '/payments/overview'
  },
  dinteroSessionProfile: {
    getPath: ({ serviceOrgId, profileId }) =>
      `/service-organizations/${serviceOrgId}/dintero/session-profiles/${profileId}`,
    getBasePath: () => '/service-organizations/:id/dintero/session-profiles/:profileId'
  },
  createDinteroSessionProfile: {
    getPath: ({ serviceOrgId }) => `/service-organizations/${serviceOrgId}/dintero/session-profiles/create`,
    getBasePath: () => '/service-organizations/:id/dintero/session-profiles/create'
  },
  tickets: {
    getPath: () => '/tickets',
    getBasePath: () => '/tickets'
  },
  ticketDetail: {
    getPath: (data) => `/tickets/${data.id}`,
    getBasePath: () => '/tickets/:id'
  },
  supportOverview: {
    getPath: () => '/support/overview',
    getBasePath: () => '/support/overview'
  },
  /** Customer contact hub inbox (queues + conversation pane). On-call schedules stay under support-schedules. */
  supportInbox: {
    getPath: () => '/support/inbox',
    getBasePath: () => '/support/inbox'
  },
  supportConversation: {
    getPath: (data) => `/support/inbox/${data.id}`,
    getBasePath: () => '/support/inbox/:id'
  },
  supportMailboxes: {
    getPath: () => '/support/mailboxes',
    getBasePath: () => '/support/mailboxes'
  },
  createSupportMailbox: {
    getPath: () => '/support/mailboxes/create',
    getBasePath: () => '/support/mailboxes/create'
  },
  supportMailboxDetail: {
    getPath: (data) => `/support/mailboxes/${data.id}`,
    getBasePath: () => '/support/mailboxes/:id'
  },
  supportSchedules: {
    getPath: () => '/support-schedules',
    getBasePath: () => '/support-schedules'
  },
  createSupportSchedule: {
    getPath: () => '/support-schedules/create',
    getBasePath: () => '/support-schedules/create'
  },
  supportScheduleDetail: {
    getPath: (data) => `/support-schedules/${data.id}`,
    getBasePath: () => '/support-schedules/:id'
  },
  editSupportSchedule: {
    getPath: (data) => `/support-schedules/${data.id}/edit`,
    getBasePath: () => '/support-schedules/:id/edit'
  }
});
